import { PHONE_DISPLAY, PHONE_TEL } from "@/config/site";
import { Phone, Car, Gem, Truck, Wrench } from "lucide-react";
import { useReveal } from "@/hooks/useReveal";

const tiers = [
  {
    icon: Car,
    name: "Standard Sedan / Coupe",
    price: "$195",
    priceUnit: "hook-up",
    perMile: "+ $5 per mile",
    desc: "Everyday sedans, coupes, small SUVs, wagons.",
    featured: false,
  },
  {
    icon: Gem,
    name: "Luxury / Premium Vehicles",
    price: "$230",
    priceUnit: "hook-up",
    perMile: "+ mileage",
    desc: "Porsche, Mercedes, BMW M-line, exotic and low-clearance vehicles requiring extra care.",
    featured: true,
  },
];

const extras = [
  { icon: Truck, label: "Winch-out / Recovery", note: "Priced by difficulty & time" },
  { icon: Wrench, label: "Jump Start", note: "Quoted on the phone" },
  { icon: Wrench, label: "Lockout", note: "Quoted on the phone" },
  { icon: Wrench, label: "Tire Change", note: "Customer supplies spare" },
  { icon: Truck, label: "Motorcycle Transport", note: "Quoted per job" },
  { icon: Truck, label: "Junk Car Removal", note: "Quoted on the phone" },
];

export default function Pricing() {
  const headRef = useReveal();
  return (
    <section id="pricing" className="relative bg-black py-24 sm:py-32 border-t border-white/5 overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{ background: "radial-gradient(ellipse at 20% 0%, rgba(250,204,21,0.10) 0%, rgba(0,0,0,0) 55%)" }}
      />
      <div className="pointer-events-none absolute inset-0 bm-grain" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6">
        <div ref={headRef} className="lux-reveal flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-14">
          <div className="max-w-3xl">
            <div className="font-display uppercase tracking-[0.32em] text-yellow-400 text-xs flex items-center gap-2">
              <span className="inline-block h-1.5 w-6 bg-yellow-400 rounded-full" />
              Transparent Pricing
            </div>
            <h2 className="mt-3 font-display font-black uppercase text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Honest rates. <span className="text-yellow-400">Quoted on the phone.</span>
            </h2>
            <p className="mt-5 text-zinc-400 text-base sm:text-lg font-light max-w-2xl leading-relaxed">
              No hidden fees. No surprise add-ons. Call with your vehicle and location, and we'll give you a straight number before the truck rolls.
            </p>
          </div>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="pricing-call-btn"
            className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider px-6 py-3.5 rounded-full lux-glow-yellow transition-colors"
          >
            <Phone className="h-5 w-5" strokeWidth={3} /> Get a Quote
          </a>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tiers.map((t) => {
            const I = t.icon;
            return (
              <div
                key={t.name}
                data-testid={`pricing-tier-${t.name.split(" ")[0].toLowerCase()}`}
                className={`relative rounded-2xl p-8 sm:p-10 overflow-hidden lux-card ${
                  t.featured
                    ? "lux-glass-strong border-yellow-400/40"
                    : "lux-glass"
                }`}
              >
                {t.featured && (
                  <div
                    aria-hidden="true"
                    className="pointer-events-none absolute -top-20 -right-20 w-56 h-56 rounded-full opacity-60"
                    style={{ background: "radial-gradient(circle, rgba(250,204,21,0.45) 0%, rgba(0,0,0,0) 70%)" }}
                  />
                )}
                {t.featured && (
                  <div className="absolute top-4 right-4 lux-glass rounded-full border border-yellow-400/50 text-yellow-400 font-display uppercase tracking-[0.22em] text-[10px] px-3 py-1">
                    Premium Care
                  </div>
                )}
                <div className="relative flex items-center gap-3">
                  <div className="h-12 w-12 rounded-xl bg-yellow-400/10 border border-yellow-400/40 flex items-center justify-center">
                    <I className="h-6 w-6 text-yellow-400" strokeWidth={2} />
                  </div>
                  <h3 className="font-display font-black uppercase text-white text-xl sm:text-2xl tracking-tight">{t.name}</h3>
                </div>
                <div className="relative mt-8 flex items-baseline gap-2">
                  <span className="font-display font-black text-white text-6xl sm:text-7xl leading-none tracking-tighter">{t.price}</span>
                  <span className="font-display uppercase tracking-[0.22em] text-[11px] text-zinc-400">
                    {t.priceUnit}
                  </span>
                </div>
                <div className="relative mt-2 font-display uppercase tracking-[0.2em] text-sm text-yellow-400">
                  {t.perMile}
                </div>
                <p className="relative mt-5 text-sm text-zinc-400 font-light leading-relaxed">
                  {t.desc}
                </p>
                <a
                  href={`tel:${PHONE_TEL}`}
                  data-testid={`pricing-tier-call-${t.name.split(" ")[0].toLowerCase()}`}
                  className="relative mt-8 inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-display font-black uppercase tracking-wider px-5 py-3 rounded-full lux-glow-yellow transition-colors min-h-[48px]"
                >
                  <Phone className="h-4 w-4" strokeWidth={3} /> Call for a Quote
                </a>
              </div>
            );
          })}
        </div>

        <div className="mt-16">
          <div className="font-display uppercase tracking-[0.32em] text-yellow-400 text-xs flex items-center gap-2">
            <span className="inline-block h-1.5 w-6 bg-yellow-400 rounded-full" />
            Additional Services
          </div>
          <p className="mt-3 text-zinc-400 text-sm sm:text-base max-w-2xl font-light">
            Additional services are priced by job difficulty, time and equipment needed. We quote every job before we roll.
          </p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {extras.map((e) => {
              const I = e.icon;
              return (
                <li
                  key={e.label}
                  className="flex items-start gap-3 lux-glass rounded-xl p-4 hover:border-yellow-400/40 transition-colors"
                >
                  <div className="h-10 w-10 shrink-0 rounded-lg bg-yellow-400/10 border border-yellow-400/30 flex items-center justify-center">
                    <I className="h-5 w-5 text-yellow-400" strokeWidth={2} />
                  </div>
                  <div>
                    <div className="font-display font-black uppercase text-white text-sm">{e.label}</div>
                    <div className="text-zinc-500 text-xs mt-0.5 font-light">{e.note}</div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="mt-10 lux-glass rounded-2xl p-5 text-zinc-400 text-sm font-light leading-relaxed">
          <span className="font-display uppercase tracking-[0.22em] text-yellow-400 text-xs mr-2">Fine print:</span>
          Rates listed are base hook-up plus mileage for standard non-emergency towing during normal conditions. After-hours, holiday, storage, heavy-duty, off-road recovery, and situations requiring extra equipment may adjust final price. Every job is quoted up-front before dispatch.
        </div>
      </div>
    </section>
  );
}
